import { useState } from "react";
import { Copy, ExternalLink, RefreshCw } from "lucide-react";
import type { GatewayManifestView } from "../../api/types";

type PayloadKind = "input" | "output";
type MediaAsset = { type: "image" | "video"; url: string; label: string };

interface RequestPayloadPanelProps {
  title: string;
  kind: PayloadKind;
  icon: React.ReactNode;
  manifest?: GatewayManifestView;
  fallbackRef: string;
  hash?: string;
  loading: boolean;
  queryError: boolean;
  onRetry: () => void;
}

const inputFields: Array<[string, string]> = [
  ["model", "模型"], ["modality", "模态"], ["generation_mode", "生成方式"], ["aspect_ratio", "宽高比"],
  ["resolution", "分辨率"], ["duration_seconds", "时长（秒）"], ["output_count", "输出数量"],
  ["output_format", "输出格式"], ["response_mode", "响应方式"], ["business_identifier", "业务标识"],
];

// RequestPayloadPanel renders one bounded manifest without hiding its troubleshooting metadata.
// 展示一侧大小受限的清单内容，并保留排障所需的存储信息。
export function RequestPayloadPanel(props: RequestPayloadPanelProps) {
  const refValue = props.manifest?.ref || props.fallbackRef;
  return <section className="content-section payload-panel">
    <div className="section-heading"><div><h2>{props.title}</h2><p>{payloadSubtitle(props)}</p></div>{props.icon}</div>
    {props.loading ? <PayloadSkeleton /> : props.queryError ? <PayloadState title="内容查询失败" description="请求详情仍可查看，可以重试读取输入输出。" action={<button className="secondary-button compact" onClick={props.onRetry}><RefreshCw size={14} />重试</button>} /> : <PayloadContent kind={props.kind} manifest={props.manifest} />}
    <PayloadReference title={props.title} refValue={refValue} hash={props.hash} />
  </section>;
}

function PayloadContent({ kind, manifest }: { kind: PayloadKind; manifest?: GatewayManifestView }) {
  if (!manifest || manifest.status === "not_written") {
    return <PayloadState title="尚未写入" description={kind === "input" ? "当前请求没有保存统一输入。" : "请求尚未产生统一输出，完成后会显示在这里。"} />;
  }
  if (manifest.status === "too_large") {
    return <PayloadState title="内容超过预览上限" description="该 manifest 超过 256 KiB。为避免排障页加载过大对象，这里只保留存储引用。" />;
  }
  if (manifest.status === "unavailable") {
    return <PayloadState title="暂时无法读取" description="OSS 对象缺失、损坏或存储暂时不可用。请求状态和执行记录不受影响。" />;
  }

  const record = asRecord(manifest.content);
  const prompt = kind === "input" && typeof record?.prompt === "string" ? record.prompt : "";
  const fields = kind === "input" && record ? inputFields.flatMap(([key, label]) => isScalar(record[key]) ? [{ key, label, value: formatScalar(record[key]) }] : []) : [];
  const media = collectMedia(record, kind);
  const hasSummary = Boolean(prompt || fields.length || media.length);
  return <div className="payload-preview-body">
    {prompt ? <div className="payload-prompt"><span>提示词</span><p>{prompt}</p></div> : null}
    {fields.length ? <dl className="payload-field-grid">{fields.map((field) => <div key={field.key}><dt>{field.label}</dt><dd>{field.value}</dd></div>)}</dl> : null}
    {media.length ? <div className="payload-media-grid">{media.map((asset) => <MediaPreview key={`${asset.type}-${asset.url}`} asset={asset} />)}</div> : null}
    <details className="payload-raw" open={!hasSummary}><summary>原始 JSON</summary><pre>{stringifyJSON(manifest.content)}</pre></details>
  </div>;
}

function PayloadReference({ title, refValue, hash }: { title: string; refValue: string; hash?: string }) {
  return <div className="payload-storage"><div><span>存储引用</span><code>{refValue || "-"}</code></div>{refValue ? <button className="icon-button" onClick={() => navigator.clipboard.writeText(refValue)} aria-label={`复制${title}引用`}><Copy size={16} /></button> : null}{hash ? <div className="payload-hash"><span>Payload Hash</span><code>{hash}</code></div> : null}</div>;
}

function PayloadSkeleton() {
  return <div className="payload-skeleton" aria-label="正在读取输入输出"><span /><span /><span /></div>;
}

function PayloadState({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
  return <div className="payload-state"><strong>{title}</strong><p>{description}</p>{action}</div>;
}

function MediaPreview({ asset }: { asset: MediaAsset }) {
  const [failed, setFailed] = useState(false);
  return <figure className="payload-media">
    {!failed && asset.type === "image" ? <img src={asset.url} alt={asset.label} loading="lazy" onError={() => setFailed(true)} /> : null}
    {!failed && asset.type === "video" ? <video src={asset.url} controls preload="metadata" aria-label={asset.label} onError={() => setFailed(true)} /> : null}
    {failed ? <div className="payload-media-error">媒体预览加载失败</div> : null}
    <figcaption><span>{asset.label}</span><a href={asset.url} target="_blank" rel="noreferrer" aria-label={`新窗口打开${asset.label}`}><ExternalLink size={14} />打开原文件</a></figcaption>
  </figure>;
}

function collectMedia(record: Record<string, unknown> | null, kind: PayloadKind): MediaAsset[] {
  if (!record) return [];
  const assets: MediaAsset[] = [];
  if (kind === "output") {
    appendURLArray(assets, record.images, "image", "输出图片");
    appendURL(assets, record.video_url, "video", "输出视频");
  } else {
    appendURLArray(assets, record.reference_images, "image", "参考图片");
    appendURL(assets, record.mask_image, "image", "遮罩图片");
    appendURL(assets, record.first_frame_image, "image", "首帧图片");
    appendURL(assets, record.last_frame_image, "image", "尾帧图片");
    appendURL(assets, record.input_video, "video", "输入视频");
    const roleAssets = asRecord(record.provider_role_assets);
    Object.entries(roleAssets || {}).forEach(([role, value]) => appendURL(assets, asRecord(value)?.uri, "image", `渠道素材 ${role}`));
  }
  return assets.filter((asset, index) => assets.findIndex((candidate) => candidate.type === asset.type && candidate.url === asset.url) === index).slice(0, 20);
}

function appendURLArray(assets: MediaAsset[], value: unknown, type: MediaAsset["type"], label: string) {
  if (!Array.isArray(value)) return;
  value.forEach((item, index) => appendURL(assets, item, type, `${label} ${index + 1}`));
}

function appendURL(assets: MediaAsset[], value: unknown, type: MediaAsset["type"], label: string) {
  if (typeof value === "string" && isSafeMediaURL(value)) assets.push({ type, url: value, label });
}

function isSafeMediaURL(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function isScalar(value: unknown): value is string | number | boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function formatScalar(value: string | number | boolean) {
  return typeof value === "boolean" ? value ? "是" : "否" : String(value);
}

function stringifyJSON(value: unknown) {
  try {
    return JSON.stringify(value, null, 2) ?? "null";
  } catch {
    return "无法序列化当前内容";
  }
}

function payloadSubtitle(props: RequestPayloadPanelProps) {
  if (props.loading) return "正在读取 manifest";
  if (props.queryError) return "内容查询失败";
  switch (props.manifest?.status) {
    case "available": return "已读取 manifest 内容";
    case "too_large": return "内容超过 256 KiB";
    case "unavailable": return "manifest 暂时不可读";
    default: return "尚未写入";
  }
}

